/**
 * Tri-state rendering for attribute cells (DV-504).
 *
 * DV-503 made the backend return one `attributeValues` entry per attribute
 * bound to the document's space (`isSet: false` = bound but no value), so a
 * blank cell now has two distinct meanings:
 *
 *  - "—"   the attribute applies to the document's space but holds no value;
 *  - "N/A" the attribute is not bound to that space at all (only reachable in
 *          the All-spaces scope, whose columns span the whole catalog —
 *          single-space columns are bound by construction).
 *
 * The signal rides on the row as `_attrStates`, keyed by COLUMN key (global
 * `key`, or `loc:<name>` for space-locals — the row builders translate), and
 * carries the backend's `isSet` per bound attribute so a blank cell only
 * claims "no value set" when that is what the backend actually reported.
 * A row with no `_attrStates` map (presentational mocks, review rows) has no
 * tri-state info and keeps the legacy behavior exactly: every blank renders
 * "—", with no tooltip asserting why.
 */

import {
  EMPTY_DISPLAY,
  NO_VALUE_TOOLTIP,
  NOT_APPLICABLE_DISPLAY,
  NOT_APPLICABLE_TOOLTIP,
} from "#common/attributeValues";

/**
 * Per-column state: `"set"` = the backend reported a stored value (`isSet`),
 * `"empty"` = bound to the space with no value. A column key MISSING from the
 * map is the third state, "not applicable".
 */
export type AttrStates = Record<string, "set" | "empty">;

type AttrCellParams = {
  value?: unknown;
  data?: { _attrStates?: AttrStates };
  // AG Grid's ITooltipParams types colDef as ColDef | ColGroupDef | null; a
  // group def has no `field`, so keep the type loose and narrow at read time.
  colDef?: unknown;
};

/** What a blank cell means, once the row's state map has been consulted. */
type BlankKind =
  /** No tri-state info on the row — we cannot say which blank this is. */
  | "unknown"
  /** Bound to the document's space, no value stored. */
  | "empty"
  /** A value is stored but has no text form (e.g. a stored empty string). */
  | "valueless"
  /** Not bound to the document's space. */
  | "notApplicable";

const isBlank = (value: unknown) => value == null || value === "";

/**
 * `valueFormatter` for columns with no tri-state to report (Space, Domain, Last
 * modified): a blank is just a blank. Lives here so the two grids share one
 * copy alongside the attribute formatters rather than each defining their own.
 */
export const dashFormatter = (p: { value: unknown }): string =>
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- p.value is an opaque grid cell value, defensively stringified for display
  isBlank(p.value) ? EMPTY_DISPLAY : String(p.value);

/** The colDef's `field`, when it has one (ColGroupDef and null don't). */
const fieldOf = (colDef: unknown): string | undefined => {
  if (colDef == null || typeof colDef !== "object") return undefined;
  const field = (colDef as { field?: unknown }).field;
  return typeof field === "string" ? field : undefined;
};

/**
 * Classify a blank cell from the row's state map.
 *
 * `"set"` with a blank display value is real: the backend reports `isSet` from
 * the presence of a stored row, and a stored empty string renders blank. That
 * is NOT "no value set", so it must not claim to be — it renders the same
 * em-dash but says nothing about why.
 */
const blankKind = (p: AttrCellParams): BlankKind => {
  const states = p.data?._attrStates;
  const key = fieldOf(p.colDef);
  if (states == null || key == null) return "unknown";
  const state = states[key];
  if (state === undefined) return "notApplicable";
  return state === "set" ? "valueless" : "empty";
};

/** `valueFormatter` for attribute columns: value, "—" (empty), or "N/A". */
export const attrCellFormatter = (p: AttrCellParams): string => {
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- p.value is an opaque grid cell value, defensively stringified for display
  if (!isBlank(p.value)) return String(p.value);
  return blankKind(p) === "notApplicable" ? NOT_APPLICABLE_DISPLAY : EMPTY_DISPLAY;
};

/**
 * `tooltipValueGetter` companion: explains the blank when we know what it
 * means, and stays silent otherwise — on a real value, on a row with no
 * tri-state info (legacy/mock rows, as before this feature), and on a stored
 * value with no text form, none of which we can honestly label.
 */
export const attrCellTooltip = (p: AttrCellParams): string | undefined => {
  if (!isBlank(p.value)) return undefined;
  const kind = blankKind(p);
  if (kind === "notApplicable") return NOT_APPLICABLE_TOOLTIP;
  return kind === "empty" ? NO_VALUE_TOOLTIP : undefined;
};

/**
 * Column scopes already warned about, so a grid of N rows warns once rather
 * than once per row (row builders call `rowAttrStates` per document).
 */
const warnedScopes = new Set<string>();

/**
 * Say out loud that the feature just turned itself off. The invariant the bail
 * below guards lives only in the JSDoc on `DocumentBrowserProps.documents`, so
 * without this a consumer that violates it sees no error and no failing test —
 * only cells that never show "N/A", which is indistinguishable from a space
 * whose attributes are all bound.
 */
const warnUntrustedStates = (localKeys: string[]) => {
  if (process.env.NODE_ENV === "production") return;
  const scope = localKeys.join(", ");
  if (warnedScopes.has(scope)) return;
  warnedScopes.add(scope);
  // eslint-disable-next-line no-console
  console.warn(
    `[DocumentBrowser] Attribute tri-state disabled for this scope: no document ` +
      `state was found for any space-local column (${scope}), so empty and ` +
      'not-applicable cells all render "—". Usually this means the documents ' +
      "were mapped without `localNameByAttributeId` — see the INVARIANT on " +
      "`DocumentBrowserProps.documents`. It can also mean the space's catalog " +
      "lists a space-local attribute the backend no longer binds.",
  );
};

/**
 * Re-key a document's `attrStates` (global key / local NAME) onto column keys
 * (global key / `loc:<name>`), for the row's `_attrStates`.
 *
 * Returns undefined — falling back to legacy em-dashes for the whole row —
 * when the document carries no tri-state info, and when the map cannot be
 * trusted against this column scope: a scope with space-local columns whose
 * map knows NONE of them was mapped with different lookups than it is being
 * rendered with (`localNameByAttributeId` defaults to `{}`, so an
 * All-spaces-style mapping renders every space-local column). Distrust matters
 * because the failure is not a harmless blank — it is "N/A" asserting an
 * attribute is unused by a space that does use it. A scope with no local
 * columns has nothing to verify, so the global keys stand on their own.
 *
 * Degrading is silent to the user by design, but not to the developer: it warns
 * once per scope outside production, since a silently-disabled feature is
 * otherwise only findable by already knowing the invariant exists.
 */
export const rowAttrStates = (
  attrStates: AttrStates | undefined,
  columns: { globalKeys: string[]; locals: { key: string; localName?: string }[] },
): AttrStates | undefined => {
  if (!attrStates) return undefined;
  const states: AttrStates = {};
  columns.globalKeys.forEach((key) => {
    const state = attrStates[key];
    if (state !== undefined) states[key] = state;
  });
  let localsResolved = 0;
  columns.locals.forEach(({ key, localName }) => {
    const state = localName === undefined ? undefined : attrStates[localName];
    if (state !== undefined) {
      states[key] = state;
      localsResolved += 1;
    }
  });
  if (columns.locals.length > 0 && localsResolved === 0) {
    warnUntrustedStates(columns.locals.map(({ key }) => key));
    return undefined;
  }
  return states;
};
